import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Badge,
  Row,
  Col,
  Pagination,
} from 'react-bootstrap';
import {
  PencilSquare,
  Trash,
  CheckCircleFill,
  XCircleFill,
  PeopleFill,
  Search,
  ChevronLeft,
  ChevronRight,
  PersonBadge,
  GeoAltFill,
  PersonVcardFill
} from 'react-bootstrap-icons';
import {
  Box,
  Breadcrumbs,
  Link,
  Typography,
  Tooltip as MuiTooltip
} from "@mui/material";
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import API_BASE_URL from '../../../config/api';

/* ── Shared style tokens (matching Users page) ── */
const primaryGradient = "linear-gradient(135deg, #1a237e, #0d47a1)";
const fieldStyle = {
  borderRadius: "8px",
  border: "1.5px solid #dde4f5",
  fontSize: "0.875rem",
};
const labelStyle = { fontWeight: 600, fontSize: "0.8rem", color: "#1a237e" };

/* ── Action icon button ── */
const ActionBtn = ({ tooltip, color, onClick, disabled, children }) => (
  <MuiTooltip title={tooltip} placement="top" arrow>
    <span>
      <Button
        variant="link"
        size="sm"
        onClick={onClick}
        disabled={disabled}
        className="p-1 rounded-2"
        style={{ color, lineHeight: 1, opacity: disabled ? 0.4 : 1, minWidth: 'auto' }}
      >
        {children}
      </Button>
    </span>
  </MuiTooltip>
);

const PrisonerList = () => {
  const [prisoners, setPrisoners] = useState([]);
  const [editModal, setEditModal] = useState(false);
  const [selectedPrisoner, setSelectedPrisoner] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({ show: false, id: null });

  const [searchTerm, setSearchTerm] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const [locations, setLocations] = useState([]);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPrisoners, setTotalPrisoners] = useState(0);
  const [limit, setLimit] = useState(10);
  const [loading, setLoading] = useState(false);

  const token = localStorage.getItem("adminToken");

  const fetchPrisoners = async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        page: page,
        limit: limit
      };

      if (searchTerm) {
        params.search = searchTerm;
      }
      if (filterLocation) {
        params.location = filterLocation;
      }
      if (filterStatus !== 'all') {
        params.status = filterStatus;
      }

      const res = await axios.get(`${API_BASE_URL}/api/prisoner/getPrisoners`, {
        params: params,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPrisoners(res.data?.data || []);
      setTotalPrisoners(res.data?.pagination?.total || 0);
      setTotalPages(res.data?.pagination?.totalPages || 1);
      setCurrentPage(res.data?.pagination?.page || 1);
    } catch (err) {
      toast.error("Failed to fetch prisoners");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/location/admin/getAllLocations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const activeLocations = (response.data?.data || [])
        .filter(loc => loc.status === 'Active')
        .map(l => ({ _id: l._id, location: l.location }));
      setLocations(activeLocations);
    } catch (err) {
      toast.error("Failed to fetch locations");
    }
  };

  const deletePrisoner = async () => {
    try {
      await axios.delete(`${API_BASE_URL}/api/prisoner/deletePrisoner/${confirmDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Deleted successfully");
      setConfirmDelete({ show: false, id: null });
      fetchPrisoners(currentPage);
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const toggleStatus = async (prisoner) => {
    const newStatus = prisoner.status === 'Active' ? 'Blocked' : 'Active';
    try {
      await axios.patch(`${API_BASE_URL}/api/prisoner/changePrisonerStatus/${prisoner._id}`, {
        status: newStatus
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      fetchPrisoners(currentPage);
      toast.success(`Status changed to ${newStatus}`);
    } catch (err) {
      toast.error("Failed to update status");
      console.error(err);
    }
  };

  const handleUpdate = async () => {
    try {
      await axios.put(
        `${API_BASE_URL}/api/prisoner/updatePrisoner/${selectedPrisoner._id}`,
        selectedPrisoner,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success("Updated successfully");
      setEditModal(false);
      fetchPrisoners(currentPage);
    } catch (err) {
      toast.error("Update failed");
    }
  };

  useEffect(() => {
    fetchPrisoners();
    fetchLocations();
  }, []);

  useEffect(() => {
    fetchPrisoners(1);
  }, [searchTerm, filterLocation, filterStatus, limit]);

  useEffect(() => {
    if (currentPage !== 1) {
      fetchPrisoners(currentPage);
    }
  }, [currentPage]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== '') {
        fetchPrisoners(1);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const renderPaginationItems = () => {
    const items = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    items.push(
      <Pagination.Prev 
        key="prev" 
        onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
        disabled={currentPage === 1}
        style={{ border: 'none', background: 'transparent' }}
      >
        <ChevronLeft size={16} />
      </Pagination.Prev>
    );

    if (startPage > 1) {
      items.push(
        <Pagination.Item 
          key={1} 
          onClick={() => handlePageChange(1)}
          style={{ border: 'none' }}
        >
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        items.push(<Pagination.Ellipsis key="ellipsis-start" disabled style={{ border: 'none' }} />);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item 
          key={i} 
          active={i === currentPage}
          onClick={() => handlePageChange(i)}
          style={
            i === currentPage
              ? {
                  backgroundColor: "#1a237e",
                  borderColor: "#1a237e",
                  color: "#fff",
                }
              : { border: 'none', background: 'transparent' }
          }
        >
          {i}
        </Pagination.Item>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<Pagination.Ellipsis key="ellipsis-end" disabled style={{ border: 'none' }} />);
      }
      items.push(
        <Pagination.Item 
          key={totalPages} 
          onClick={() => handlePageChange(totalPages)}
          style={{ border: 'none' }}
        >
          {totalPages}
        </Pagination.Item>
      );
    }

    items.push(
      <Pagination.Next 
        key="next" 
        onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
        disabled={currentPage === totalPages}
        style={{ border: 'none', background: 'transparent' }}
      >
        <ChevronRight size={16} />
      </Pagination.Next>
    );

    return items;
  };

  return (
    <Box sx={{ p: 3.5, background: "#f0f4ff", minHeight: "100vh" }}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 2.5, fontSize: "0.8rem" }}>
        <Link underline="hover" color="inherit" href="/dashboard">
          Dashboard
        </Link>
        <Typography
          color="primary"
          sx={{ fontSize: "0.8rem", fontWeight: 600 }}
        >
          Student Management
        </Typography>
      </Breadcrumbs>

      {/* Page header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: "11px",
              background: primaryGradient,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 12px rgba(13,71,161,0.3)",
            }}
          >
            <PersonBadge size={20} color="#fff" />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 800, color: "#1a237e", lineHeight: 1.2 }}
            >
              Student Management
            </Typography>
            <Typography variant="caption" sx={{ color: "#7b8ab8" }}>
              {totalPrisoners} total students
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Filters */}
      <Box
        sx={{
          background: "#fff",
          borderRadius: "12px",
          p: 2,
          mb: 2.5,
          border: "1px solid #e4eaf8",
          boxShadow: "0 1px 4px rgba(13,71,161,0.05)",
        }}
      >
        <Row className="g-2 align-items-center">
          <Col md={4}>
            <Box position="relative">
              <Search
                size={13}
                style={{
                  position: "absolute",
                  left: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#aab4cc",
                  zIndex: 1,
                }}
              />
              <Form.Control
                style={{ ...fieldStyle, paddingLeft: 30 }}
                placeholder="Search by name or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Box>
          </Col>
          <Col md={3}>
            <Form.Select
              style={fieldStyle}
              value={filterLocation}
              onChange={(e) => setFilterLocation(e.target.value)}
            >
              <option value="">All Locations</option>
              {locations.map(loc => (
                <option key={loc._id} value={loc._id}>{loc.location}</option>
              ))}
            </Form.Select>
          </Col>
          <Col md={3}>
            <Form.Select
              style={fieldStyle}
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="blocked">Blocked</option>
            </Form.Select>
          </Col>
        </Row>
      </Box>

      {/* Table */}
      <Box
        sx={{
          background: "#fff",
          borderRadius: "14px",
          border: "1px solid #e4eaf8",
          overflow: "hidden",
          boxShadow: "0 2px 12px rgba(13,71,161,0.06)",
        }}
      >
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            gap={2}
            py={8}
          >
            <div
              className="spinner-border"
              style={{
                color: "#0d47a1",
                width: "2.5rem",
                height: "2.5rem",
                borderWidth: "0.25rem"
              }}
              role="status"
            >
              <span className="visually-hidden">Loading...</span>
            </div>
            <Typography variant="caption" color="text.secondary">
              Loading Student...
            </Typography>
          </Box>
        ) : (
          <Table hover className="mb-0 align-middle">
            <thead style={{ background: primaryGradient }}>
              <tr>
                {["#", "Student ID", "Student Name", "Location", "Instructor", "Status", "Actions"].map((h) => (
                  <th
                    key={h}
                    style={{
                      color: "rgba(255,255,255,0.85)",
                      fontWeight: 700,
                      fontSize: "0.72rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.07em",
                      padding: "13px 16px",
                      border: "none",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {prisoners.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-5 text-muted">
                    No Student found.
                  </td>
                </tr>
              ) : (
                prisoners.map((p, idx) => (
                  <tr
                    key={p._id}
                    style={{ borderBottom: "1px solid #f0f4ff" }}
                  >
                    <td
                      style={{
                        color: "#aab4cc",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                      }}
                    >
                      {(currentPage - 1) * limit + idx + 1}
                    </td>
                    <td style={{ fontSize: "0.82rem", color: "#6b7a99" }}>
                      {p.prisonerId}
                    </td>
                    <td style={{ fontWeight: 600, color: "#1a237e" }}>
                      {p.prisonerName}
                    </td>
                    <td style={{ fontSize: "0.82rem", color: "#6b7a99" }}>
                      {p.location?.location || 'N/A'}
                    </td>
                    <td style={{ fontSize: "0.82rem", color: "#6b7a99" }}>
                      {p.instructorId?.name || 'N/A'}
                    </td>
                    <td>
                      <Badge
                        bg={p.status === 'Active' ? 'success' : 'danger'}
                        className="px-3 py-2 rounded-pill"
                        style={{ fontSize: '0.75rem' }}
                      >
                        {p.status}
                      </Badge>
                    </td>
                    <td>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <ActionBtn
                          tooltip="Edit"
                          color="#f57f17"
                          onClick={() => {
                            setSelectedPrisoner(p);
                            setEditModal(true);
                          }}
                        >
                          <PencilSquare size={16} />
                        </ActionBtn>
                        <ActionBtn
                          tooltip={p.status === 'Active' ? 'Deactivate' : 'Activate'}
                          color={p.status === 'Active' ? "#c62828" : "#2e7d32"}
                          onClick={() => toggleStatus(p)}
                        >
                          {p.status === 'Active' ? (
                            <XCircleFill size={17} />
                          ) : (
                            <CheckCircleFill size={17} />
                          )}
                        </ActionBtn>
                        <ActionBtn
                          tooltip="Delete"
                          color="#c62828"
                          onClick={() => setConfirmDelete({ show: true, id: p._id })}
                        >
                          <Trash size={16} />
                        </ActionBtn>
                      </Box>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        )}

        {/* Pagination */}
        {totalPrisoners > 0 && (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            px={2.5}
            py={1.8}
            sx={{ borderTop: "1px solid #f0f4ff", background: "#fafbff" }}
          >
            <Typography variant="caption" sx={{ color: "#7b8ab8" }}>
              Showing{" "}
              <b style={{ color: "#1a237e" }}>
                {(currentPage - 1) * limit + 1}
              </b>
              –
              <b style={{ color: "#1a237e" }}>
                {Math.min(currentPage * limit, totalPrisoners)}
              </b>{" "}
              of <b style={{ color: "#1a237e" }}>{totalPrisoners}</b>
            </Typography>
            <Pagination size="sm" className="mb-0">
              {renderPaginationItems()}
            </Pagination>
          </Box>
        )}
      </Box>

      {/* Edit Modal */}
      <Modal
        show={editModal}
        onHide={() => setEditModal(false)}
        centered
      >
        <Modal.Header
          closeButton
          className="text-white border-0"
          style={{ background: primaryGradient }}
        >
          <Modal.Title
            className="fw-bold d-flex align-items-center gap-2"
            style={{ fontSize: "0.95rem" }}
          >
            <PersonVcardFill size={18} /> Edit Student Profile
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: "#f7f9ff", padding: '1.5rem' }}>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label style={labelStyle}>Student Name</Form.Label>
              <Form.Control
                style={fieldStyle}
                value={selectedPrisoner?.prisonerName || ''}
                onChange={(e) =>
                  setSelectedPrisoner({ ...selectedPrisoner, prisonerName: e.target.value })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={labelStyle}>Location</Form.Label>
              <Form.Select
                style={fieldStyle}
                value={selectedPrisoner?.location?._id || ''}
                onChange={(e) => {
                  const loc = locations.find(l => l._id === e.target.value);
                  setSelectedPrisoner({
                    ...selectedPrisoner,
                    location: loc || { location: '' }
                  });
                }}
              >
                <option value="">Select Location</option>
                {locations.map(loc => (
                  <option key={loc._id} value={loc._id}>
                    {loc.location}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer style={{ background: "#f7f9ff", borderTop: '1px solid #e4eaf8' }}>
          <Button
            variant="outline-secondary"
            onClick={() => setEditModal(false)}
            style={{ borderRadius: '8px' }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUpdate}
            style={{ background: primaryGradient, border: 'none', borderRadius: '8px' }}
          >
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Confirm Delete Modal */}
      <Modal
        show={confirmDelete.show}
        onHide={() => setConfirmDelete({ show: false, id: null })}
        centered
      >
        <Modal.Header
          closeButton
          className="text-white border-0"
          style={{ background: primaryGradient }}
        >
          <Modal.Title
            className="fw-bold d-flex align-items-center gap-2"
            style={{ fontSize: "0.95rem" }}
          >
            <Trash size={18} /> Confirm Deletion
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: "#f7f9ff", padding: '1.5rem' }}>
          <Box
            sx={{
              background: "#fff3e0",
              border: "1px solid #ffe0b2",
              borderRadius: "8px",
              p: 2,
              fontSize: "0.9rem",
              color: "#e65100",
            }}
          >
            ⚠ Are you sure you want to delete this Student? This action cannot be undone.
          </Box>
        </Modal.Body>
        <Modal.Footer style={{ background: "#f7f9ff", borderTop: '1px solid #e4eaf8' }}>
          <Button
            variant="outline-secondary"
            onClick={() => setConfirmDelete({ show: false, id: null })}
            style={{ borderRadius: '8px' }}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={deletePrisoner}
            style={{ borderRadius: '8px' }}
          >
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Box>
  );
};

export default PrisonerList;